import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CropAndCropAid, LegalRestriction } from '@next/arable-lib/advice';
import { CropVariety } from '@next/arable-lib/crop';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { map, Observable } from 'rxjs';
import { Environment } from 'src/environments/environment.model';
import { CropVarietiesByCropId } from '../models';

@Injectable({
  providedIn: 'root',
})
export class CropVarietyService {
  private readonly http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  private readonly arableAdminBaseUrl = `${this.env.APIM_BASE_URL}/arable-service`;

  getCropVarietiesByCropId(cropId: number): Observable<CropVariety[]> {
    return this.http
      .get<CropVariety[]>(`${this.arableAdminBaseUrl}/cropvariety/cropId/${cropId}`);
  }

  getCropVarietiesByCropIds(cropIds: number[]): Observable<CropVariety[]> {
    const uniqueCropIds = [...cropIds]
      .sort((a, b) => a - b)
      .filter((item, pos, ary) => !pos || item !== ary[pos - 1]);

    return this.http.get<CropVariety[]>(
      `${this.arableAdminBaseUrl}/cropvariety/cropids?${this.getParamString('cropIds', uniqueCropIds)}`,
    );
  }

  getGroupedCropVarietiesByCropIds(cropIds: number[]): Observable<CropVarietiesByCropId[]> {
    return this.getCropVarietiesByCropIds(cropIds)
      .pipe(map((cropVarieties) => this.groupCropVarietiesByCropId(cropVarieties)));
  }

  getLegalRestrictions(cropAndCropAids: CropAndCropAid[]) {
    return this.http
      .post<{ legalRestrictions: LegalRestriction[] }>(
        `${this.arableAdminBaseUrl}/legal-restriction/cropandcropaid`,
        { cropAndCropAids },
      )
      .pipe(map(({ legalRestrictions }) => legalRestrictions));
  }

  private getParamString(key: string, values: number[] | string[]): string {
    return values.map((value) => `${key}=${value}`).join('&');
  }

  private groupCropVarietiesByCropId(cropVarieties: CropVariety[]): CropVarietiesByCropId[] {
    const groupedCropVarieties = new Map<number, CropVarietiesByCropId>();

    for (const cropVariety of cropVarieties) {
      const existingGroup = groupedCropVarieties.get(cropVariety.cropId);

      if (existingGroup) {
        existingGroup.cropVarieties.push(cropVariety);
        continue;
      }

      groupedCropVarieties.set(cropVariety.cropId, {
        cropId: cropVariety.cropId,
        cropVarieties: [cropVariety],
      });
    }

    return [...groupedCropVarieties.values()];
  }
}
